'use client';

import * as React from 'react';
import { addDays, format, subDays } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

export type DateRangePickerProps = {
  className?: string;
  buttonClassName?: string;
  onRangeChange?: (range: DateRange | undefined) => void;
  initialDateRange?: DateRange;
  maxDate?: Date;
};

export function DateRangePicker({
  className,
  buttonClassName,
  onRangeChange,
  initialDateRange,
  maxDate,
}: DateRangePickerProps) {
  const [date, setDate] = React.useState<DateRange | undefined>({
    from: initialDateRange?.from ?? subDays(new Date(), 14), // Current date
    to: initialDateRange?.to ?? new Date(), // 2 weeks forward
  });
  const [isOpen, setIsOpen] = React.useState(false);

  // Update internal state when initialDateRange prop changes
  React.useEffect(() => {
    if (initialDateRange?.from && initialDateRange?.to) {
      setDate(initialDateRange);
    }
  }, [initialDateRange]);

  // Predefined date ranges
  const predefinedRanges = [
    {
      name: '2 Weeks',
      getRange: () => ({ from: addDays(new Date(), -14), to: new Date() }),
    },
    {
      name: '1 Month',
      getRange: () => ({ from: addDays(new Date(), -30), to: new Date() }),
    },
    {
      name: '3 Months',
      getRange: () => ({ from: addDays(new Date(), -90), to: new Date() }),
    },
    {
      name: '6 Months',
      getRange: () => ({ from: addDays(new Date(), -180), to: new Date() }),
    },
  ];

  const handleRangeChange = (newRange: DateRange | undefined) => {
    setDate(newRange);
    if (onRangeChange) {
      onRangeChange(newRange);
    }
  };

  const handlePredefinedRange = (rangeIndex: number) => {
    const newRange = predefinedRanges[rangeIndex].getRange();
    handleRangeChange(newRange);
    setIsOpen(false); // Close popover after selection
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // When closing, ensure the calendar's internal state is reset
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <div className={cn('w-fit min-w-0', className)}>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          render={
          <Button
            id="date"
            size="default"
            variant="outline"
            className={cn(
              'inline-flex h-9 w-auto max-w-full shrink-0 items-center gap-2 px-3 text-left font-normal text-ds-text-default hover:bg-ds-gray-subtlest bg-ds-surface-white ds-text-body-sm-medium',
              buttonClassName,
              !date && 'text-ds-text-subtle'
            )}
          >
            <CalendarIcon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 whitespace-nowrap">
              {date?.from ? (
                date.to ? (
                  <>
                    {format(date.from, 'd MMM')} - {format(date.to, 'd MMM')}
                  </>
                ) : (
                  format(date.from, 'd MMM')
                )
              ) : (
                'Pick a date range'
              )}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger
                nativeButton={false}
                render={
                <span
                  role="button"
                  tabIndex={0}
                  className={cn(
                    buttonVariants({ variant: 'ghost', size: 'sm' }),
                    '-mr-1 inline-flex p-0 h-auto bg-transparent hover:bg-transparent focus-visible:outline-none focus-visible:ring-1'
                  )}
                >
                  <ChevronDown className="h-4 w-4 text-ds-text-subtle" />
                </span>
                }
              />

              <DropdownMenuContent
                align="end"
                className="bg-ds-surface-white border-ds-border-subtlest text-ds-text-default shadow-md"
              >
                {predefinedRanges.map((range, index) => (
                  <DropdownMenuItem
                    key={range.name}
                    onClick={() => handlePredefinedRange(index)}
                    className="cursor-pointer ds-text-body-sm-medium text-ds-text-default hover:bg-ds-gray-subtlest"
                  >
                    {range.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </Button>
          }
        />
        <PopoverContent
          className="w-fit p-0 bg-ds-surface-white border-ds-border-subtlest text-ds-text-default shadow-md"
          align="start"
        >
          {isOpen && (
            <Calendar
              autoFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={handleRangeChange}
              numberOfMonths={2}
              showOutsideDays={false}
              disabled={maxDate ? { after: maxDate } : undefined}
              className="text-ds-text-default"
              key={date?.from?.toISOString() || 'calendar'}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
